import {
  AdgroupFormData,
  CampaignFormData,
  ShopListItem,
  CatalogListItem,
  FullNormalFormData,
  PRODUCT_SPECIFIC_TYPE,
  PRODUCTS_PSA_MAX_NUM,
  StoreType,
} from '@tt4b/ttam-enums';
import { Ref, ComputedRef, ref, computed, watch } from '@vue/composition-api';
import {
  useCatalogStore,
  useGetSpuProductApi,
  useProductSetStore,
  useRequest,
  useShopStore,
  isDisabledProduct,
  useStdAdgroupIs,
  useStdCampaignIs,
} from '@tt4b/ttam-hooks';
import { i18n } from '@tt4b/ttam-i18n';
import { ProductListItemType } from './types';

export function usePropsShopInfo(storeInfo: Ref<ShopListItem>) {
  const { id: shopId, type: shopType, bc_id: bcId, name: shopName, thumbnail_url: thumbnailUrl } = storeInfo.value;
  return {
    shopId,
    shopType,
    bcId,
    shopName,
    thumbnailUrl,
  };
}

export function useShopInfo(opt: { adgroupData: Ref<AdgroupFormData> }): ComputedRef<ShopListItem> {
  const { selectedShop } = useShopStore({
    adgroupFormData: opt.adgroupData,
  });
  return computed(() => selectedShop.value || ({} as ShopListItem));
}

/**
 * catalogType:
 *  含义: catalog类型，用于区分汽车广告
 *  是否必传: 非必传参数，未传的场景下默认值为10
 */
export function useCatalogInfo(opt: {
  adgroupData: Ref<AdgroupFormData>;
  catalogType?: Ref<number>;
}): ComputedRef<CatalogListItem> {
  const { catalogType = ref(StoreType.ECOM) } = opt;

  const { selectedCatalog } = useCatalogStore({
    adgroupFormData: opt.adgroupData,
    catalogType,
  });
  const catalogInfo = computed(() => selectedCatalog.value || ({} as CatalogListItem));
  return catalogInfo;
}

export function useDisabledAlert(opt: { spuDetailList: Ref<{ isUnavailable: boolean }[]> }) {
  const { spuDetailList } = opt;
  const len = computed(() => spuDetailList.value.filter(i => i.isUnavailable).length);

  const disabledText = computed(() => {
    if (len.value === 0) {
      return '';
    } else if (len.value === spuDetailList.value.length) {
      return i18n.t('module_creative_all_products_not_available');
    } else if (len.value === 1) {
      return i18n.t('module_creative_products_not_available');
    } else {
      return i18n.t('module_creative_multi_products_not_available', [len.value]);
    }
  });

  const alertType = computed(() => {
    if (len.value === 0) {
      return '';
    } else if (len.value < spuDetailList.value.length) {
      return 'warning';
    } else {
      return 'error';
    }
  });

  return {
    alertType,
    disabledText,
  };
}

export function useGetShopProductsDetails(opt: {
  spuIdList?: ComputedRef<Array<string>> | Ref<Array<string>>;
  shopInfo: ComputedRef<ShopListItem> | Ref<ShopListItem>;
  limit?: number;
  page?: Ref<number>;
  search?: Ref<{
    field: string;
    value: string;
  }>;
}) {
  const { spuIdList, shopInfo, limit = 20, page = ref(1), search } = opt;
  const shopId = computed(() => shopInfo.value.id);
  const param = computed(() => ({
    shopId: shopInfo.value.id,
    catalogId: shopInfo.value.platform_id,
    bcId: shopInfo.value.bc_id,
    search: search?.value,
    page_info: {
      offset: (page.value - 1) * limit,
      pageSize: limit,
    },
    ...(spuIdList?.value
      ? {
          search: {
            field: 'spuID',
            value: spuIdList.value,
          },
        }
      : {}),
  }));
  const { data, isLoading, run, reset } = useRequest(useGetSpuProductApi, param, {
    manual: true,
  });

  watch(
    param,
    val => {
      const { shopId: _shopId, catalogId, bcId } = val || {};
      if (_shopId && catalogId && bcId) {
        run();
      }
    },
    {
      immediate: true,
    },
  );

  return {
    data,
    isLoading,
    run,
    reset,
  };
}

export function useProductListForShop(opt: { creativeData: Ref<FullNormalFormData>; emit }) {
  const { creativeData, emit } = opt;
  const spuDetailList = computed(() => creativeData.value.spuDetailList || []);
  const spuList = computed(() => creativeData.value.spuList || []);
  const productListDataSource = computed(() =>
    spuList.value?.map(id => {
      const itemInList = spuDetailList.value.find(item => item?.spu_id === id);
      if (itemInList) {
        return {
          id: itemInList.spu_id,
          name: itemInList.title,
          img: itemInList.picture,
          disabled: itemInList.isDisabled || false,
        };
      } else {
        return {
          id: '',
          name: id,
          disabled: true,
        };
      }
    }),
  );
  const spuMap = computed(() => {
    const kvArray = spuDetailList.value.map(item => [item?.spu_id, item]);
    return new Map(kvArray as Iterable<readonly [unknown, unknown]>);
  });
  const onProductListChange = (productList: Array<ProductListItemType>) => {
    const newSpuDetailList = productList.map(item => spuMap.value.get(item.id));
    emit('productListChange', newSpuDetailList);
  };
  return {
    productListDataSource,
    onProductListChange,
  };
}

export function useProductListForShowcase(opt: { creativeData: Ref<FullNormalFormData>; emit }) {
  const { creativeData, emit } = opt;
  const spuDetailList = computed(() => creativeData.value.spuDetailList || []);
  const spuList = computed(() => creativeData.value.showcaseSpuList?.map?.(item => item.spuId) ?? []);
  const productListDataSource = computed(() =>
    spuList.value?.map(id => {
      const itemInList = spuDetailList.value.find(item => item?.spu_id === id);
      // item in list
      if (itemInList) {
        return {
          id: itemInList.spu_id,
          name: itemInList.title,
          img: itemInList.picture,
          disabled: itemInList.isDisabled || false,
        };
      } else {
        //  返回占位
        return {
          id: '',
          name: id,
          disabled: true,
        };
      }
    }),
  );
  const showcaseSpuMap = computed(() => {
    const kvArray = spuDetailList.value.map(item => [item?.spu_id, item]);
    return new Map(kvArray as Iterable<readonly [unknown, unknown]>);
  });
  // product 变化
  const onProductListChange = (productList: Array<ProductListItemType>) => {
    const newSpuDetailList = productList.map(item => showcaseSpuMap.value.get(item.id));
    emit('productListChange', newSpuDetailList);
  };
  return {
    productListDataSource,
    onProductListChange,
  };
}

export function useProductListForCatalog(opt: { creativeData: Ref<FullNormalFormData>; emit }) {
  const { creativeData, emit } = opt;
  const dpaProductsList = computed(() => creativeData.value.dpaProductsList || []);
  const dpaProducts = computed(() => creativeData.value.dpaProducts || []);
  const productListDataSource = computed(() =>
    dpaProducts.value?.map(id => {
      const itemInList = dpaProductsList.value.find(item => item?.productID === id);
      if (itemInList) {
        return {
          id: itemInList.OuterID,
          name: itemInList.name,
          img: itemInList.image?.url,
          disabled: isDisabledProduct(itemInList),
        };
      } else {
        return {
          id: '',
          name: id,
          disabled: true,
        };
      }
    }),
  );
  // const productListDataSource = computed(() =>
  //   dpaProductsList.value.map(item => ({
  //     id: item.OuterID,
  //     name: item.name,
  //     img: item.image?.url,
  //     disabled: isDisabledProduct(item),
  //   }))
  // );
  const productsMap = computed(() => {
    const kvArray = dpaProductsList.value.map(item => [item?.OuterID, item]);
    return new Map(kvArray as Iterable<readonly [unknown, unknown]>);
  });
  const onProductListChange = (productList: Array<ProductListItemType>) => {
    const newDpaProductsList = productList.map(item => productsMap.value.get(item.id));
    emit('productListChange', newDpaProductsList);
  };
  return {
    productListDataSource,
    onProductListChange,
  };
}

export function useListActions(emit) {
  const openDrawer = () => {
    emit('openDrawer');
  };
  const clear = () => {
    emit('clear');
  };
  return {
    openDrawer,
    clear,
  };
}

// 获取tea上传的一些参数的公共方法
export function getCreateModeTeaParams(options: { isEdit: Ref<boolean>; isCopy: Ref<boolean> }) {
  const { isCopy, isEdit } = options;

  let scenario = 'Create new';

  if (isCopy.value) {
    scenario = 'Copy';
  } else if (isEdit.value) {
    scenario = 'Edit';
  } else if (/source=campaign_list/.test(window?.location?.search)) {
    // 编辑草稿的时候，url都会带上 source=campaign_list
    scenario = 'Draft';
  }

  return {
    scenario,
  };
}

/**
 * catalogType:
 *  含义: catalog类型，用于区分汽车广告，对应StoreType枚举
 *  是否必传: 非必传参数，未传的场景下默认值为10
 */
export function usePropsSetList(options: { adgroupData: Ref<AdgroupFormData>; catalogType?: Ref<number> }) {
  const { adgroupData, catalogType = ref(StoreType.ECOM) } = options;
  const { productSetList, getProductSet, isLoading } = useProductSetStore({
    adgroupFormData: adgroupData,
    catalogType,
  });

  return {
    isLoadingPsetList: isLoading,
    getProductSet,
    productSetList,
  };
}

export function useSpecTypeOpts(opt: { adgroupData: Ref<AdgroupFormData>; campaignData: Ref<CampaignFormData> }) {
  const { campaignData, adgroupData } = opt;
  const { isCLA, isPSA } = useStdAdgroupIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });
  const { isLeadAdsAutoInventoryAds } = useStdCampaignIs({
    campaignFormData: campaignData,
  });
  const allType = () => ({
    value: PRODUCT_SPECIFIC_TYPE.ALL,
    label: i18n.t('module_creative_product_details_all_products'),
    description: i18n.t('module_creative_product_details_all_products_tips'),
  });
  const setType = () => ({
    value: PRODUCT_SPECIFIC_TYPE.PRODUCT_SET,
    label: i18n.t('module_creative_product_details_product_set'),
    description: i18n.t('module_creative_product_details_product_set_tips'),
  });
  const productType = () => ({
    value: PRODUCT_SPECIFIC_TYPE.PRODUCTS,
    label: i18n.t('module_creative_product_details_specify_products'),
    description: i18n.t('module_cla_product_details_specify_products_tips', {
      0: isCLA.value ? 50 : 20,
    }),
  });
  const options = computed(() => {
    if (isLeadAdsAutoInventoryAds.value) {
      return [allType(), setType()];
    }

    return isPSA.value
      ? [
          {
            ...allType(),
            description: i18n.t('module_creative_psa_product_details_all_products_tips'),
          },
          {
            ...productType(),
            description: i18n.t('module_psa_product_details_specify_products_tips', { 0: PRODUCTS_PSA_MAX_NUM }),
          },
        ]
      : [allType(), setType(), productType()];
  });

  return {
    options,
  };
}
